import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode, SyntheticEvent } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StaffAutoComplete } from "../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import type { Nurse } from "../../utils/models";
import { headerCardStyle, headingSx } from "./v2/utils";

/**
 * Fixed height of the header band, in px.
 *
 * The tallest control a header can hold is `StaffAutoComplete`: a 40px MUI `size="small"`
 * outlined input plus the 19px helper-text line `SystemValidationTextField` always reserves
 * (rendered at `opacity: 0` when there's no message) — 59px, inside `headerCardStyle`'s 16px
 * vertical padding.
 *
 * Pinning it is the point: a layout with NO staff search (the People axis) would otherwise
 * render a band ~30px shorter than one with it, so the title, the tabs and the whole grid
 * under them shifted vertically on every tab switch.
 */
const HEADER_MIN_HEIGHT = 91;

/** The selection a layout filters by, and its change handler. The layout owns this state. */
export type StaffFilter = {
	value: SystemAutoCompleteOption<Nurse> | null;
	onChange: (event: SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => void;
};

/**
 * The header's staff search. Split out as its own component so the option list is built ONLY
 * when a layout actually shows a search — a conditional hook is illegal, so gating the memo
 * means gating the component that owns it.
 */
const HeaderStaffSearch = ({ value, onChange }: StaffFilter) => {
	const { chosenDepartment } = useChosenDepartment();
	const { roster, isLoading, isError } = useRosterLookup(chosenDepartment.department_id);
	const formatPersonName = useFormatPersonName();
	const { t } = useTranslation();
	// Every layout offered the same list: all department nurses, labeled by the department's name order. It lives
	// here rather than in each layout's state hook, where it was written three times over.
	const options = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		// DEFAULT: no options until the roster loads; the field is disabled meanwhile and says so if the load fails.
		() => (roster ?? []).map((nurse) => ({ value: nurse, label: formatPersonName(nurse) })),
		[roster, formatPersonName],
	);
	// Coverage reads the roster only here, so this field is the one place a failed load can show there.
	return (
		<StaffAutoComplete
			nurses={options}
			value={value}
			onChange={onChange}
			disabled={isLoading || isError}
			helperText={isError ? t("profile.roster.unavailable") : undefined}
		/>
	);
};

type Props = {
	/**
	 * Staff search wiring, rendered right after the title. Omit it for a layout with no staff
	 * search (the People axis, whose rows ARE people and whose toolbar owns a name search) —
	 * the band keeps its height either way.
	 */
	staffFilter?: StaffFilter;
	/**
	 * The layout tab switcher (Weekly / Consecutive / Coverage / People). Built by
	 * `ScheduleTemplatePageWrapper`, which owns the selected-tab state, and threaded down
	 * through the layout — this component can't call the hook itself without forking that state.
	 */
	trailing?: ReactNode;
	/** Actions (export, …), pushed to the far right edge. Genuinely per-layout: Weekly exports, Coverage's is a disabled placeholder, Consecutive has none. */
	actions?: ReactNode;
	dataTestId?: string;
};

/**
 * The Schedule Template page header band — shared by all four layouts (Weekly, Consecutive,
 * Coverage, People).
 *
 * It exists because each layout used to hand-roll its own: three different sets of padding,
 * gap, shadow, background and title type that had drifted apart, so the title and the tab
 * switcher visibly jumped as you moved between tabs.
 *
 * Everything invariant is owned HERE, not passed in — the chrome, the title (all four layouts
 * are views of one page, so they can't disagree on its name) and the staff search including its
 * option list. A layout supplies only what genuinely differs: the selection its own grid filters
 * by, its actions, and the tab switcher it received from the route wrapper.
 *
 * TODO: Also embed into people tab.
 */
export const ScheduleTemplateHeader: FC<Props> = ({ staffFilter, trailing, actions, dataTestId }) => {
	const { t } = useTranslation();
	return (
		// The Annie walkthrough anchor is owned here rather than passed in by each layout: every layout
		// renders this same band, the step's copy is about the PAGE, and threading it through three call
		// sites meant the Coverage and Consecutive layouts silently had no anchor — which dropped the step
		// and took the tour below its floor.
		<Box data-testid={dataTestId} data-annie-target="master-schedule-header" sx={[headerCardStyle, { minHeight: HEADER_MIN_HEIGHT }] as SxProps<Theme>}>
			<Typography sx={headingSx}>{t("schedule.template.title")}</Typography>
			{staffFilter && <HeaderStaffSearch value={staffFilter.value} onChange={staffFilter.onChange} />}
			{trailing}
			{actions && <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>{actions}</Box>}
		</Box>
	);
};
